/**
 * Classification of an upload/extraction failure, decoupled from any i18n
 * namespace or UI surface (toast vs inline Alert). Each call site maps the kind
 * to its own message + severity.
 */
export type UploadFailureKind = 'rate-limit' | 'file-too-large' | 'network' | 'technical';

/**
 * Extracts the HTTP status from a caught upload error.
 *
 * ky surfaces server responses through an `HTTPError` that carries a `response`
 * object. A native fetch/transport failure (Safari "Load failed", timeout,
 * offline) propagates the raw error WITHOUT a `response`, so the status is
 * `undefined` — that is the signal the request never completed at the HTTP layer.
 */
export function getHttpStatusFromError(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/**
 * Classifies an upload failure from its HTTP status.
 *
 * - 429: AI extraction rate limit reached.
 * - 413: file rejected by the server as too large.
 * - no HTTP status (transport/network failure): connection lost — the request
 *   never reached the server (Safari "Load failed", timeout, offline). Callers
 *   should tell the user to check the network and retry, NOT show a generic
 *   "technical problem" (which wrongly implies the server/AI failed).
 * - any other server status: generic technical failure.
 */
export function classifyUploadFailure(httpStatus: number | undefined): UploadFailureKind {
  if (httpStatus === 429) {
    return 'rate-limit';
  }
  if (httpStatus === 413) {
    return 'file-too-large';
  }
  if (httpStatus === undefined) {
    return 'network';
  }
  return 'technical';
}

/**
 * Whether an upload failure is worth a Sentry report. Network/transport
 * failures (Safari "Load failed", timeout, offline) are flaky-connectivity
 * noise already surfaced to the user via toast — not actionable engineering
 * signal. Everything else (rate-limit, file-too-large, server errors) is.
 */
export function shouldReportUploadError(error: unknown): boolean {
  return classifyUploadFailure(getHttpStatusFromError(error)) !== 'network';
}

export type NetworkErrorRecoveryOutcome = 'recovered' | 'still-failed';

/**
 * A network/timeout upload failure can be a false alarm: ky aborts client-side
 * at 120s, but the server keeps running and completes the extraction anyway.
 * Given the draft's extraction status refetched right after the failure,
 * decide whether to treat it as a success (COMPLETED/PENDING — the server is
 * alive) or as a real failure (IDLE/FAILED, or the refetch itself failed).
 */
export function resolveNetworkErrorRecovery(
  extractionStatus: string | null | undefined,
): NetworkErrorRecoveryOutcome {
  return extractionStatus === 'COMPLETED' || extractionStatus === 'PENDING'
    ? 'recovered'
    : 'still-failed';
}

/** 409 from the photo upload in append mode: the PDF uploaded earlier has no page previews yet. */
export function isPdfPagesNotReady(httpStatus: number | undefined): boolean {
  return httpStatus === 409;
}
